<template>
  <div class="has-radius-top-left mt-3 Project-list">
    <div class="list-view">
      <table-toolbar
        searchPlaceHolder="Search Projects"
        :toolbarOpts="tableColOpts.toolBar"
        @handleAction="handleAction">
        <template v-slot:filterToggle>
          <o-switch
            v-model="showActiveOnly"
            @update:modelValue="onToggleActiveOnly">
            <span class="has-text-black-bis is-size-3 is-italic has-text-weight-bold">
              Only Active
            </span>
          </o-switch>
        </template>
      </table-toolbar>
      <data-table
        :apiMode="true"
        :tableOpts="tableColOpts"
        :loadData="loadData"
        @cell-clicked="onCellClicked"
        :key="reRender"
      />
    </div>
  </div>
  <create-project v-if="showCreateProjectModal" :active="showCreateProjectModal" @close="showCreateProjectModal = false"/>
  <notify-users
    v-if="showNotifyUsersModal"
    :isActive="showNotifyUsersModal"
    :card="selectedNotifyCard"
    @close="showNotifyUsersModal = false"
  />
  <activity-log
    v-if="isActivityLogOpen"
    :isActive="isActivityLogOpen"
    :id="activityDetail.id"
    :projectId="activityDetail.projectId"
    :orderName="activityDetail.orderName"
    @update:isActive="isActivityLogOpen = false"
  />
  <ActivitySlider
    v-if="isActivityActive"
    :isActive="isActivityActive"
    :id="companyId"
    module="Project"
    :company="companyId"
    @update:isActive="isActivityActive = $event"
  />
  <excel-import
    v-if="showExcelModal"
    :isExcelModalActive="showExcelModal"
    :customExcelCols="projectExcelCols"
    :type="'projects'"
    importModule="projects-import"
    :uniqueFields="['proId']"
    :validateExcelItem="validateProject"
    :mapData="mapData"
    :saveExcelItem="saveExcelItem"
    modalHeaderTitle="Import Projects from Excel"
    @close-modal="showExcelModal = false"
    @refresh-table="refreshTable"
  />
</template>
<script>
import { onBeforeMount, reactive, toRefs, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { groupBy, isEmpty, map } from 'lodash';
import { useToast } from 'vue-toastification';
import DataTable from '@/components/table-fields/DataTable.vue';
import TableToolbar from '@/components/table-fields/TableToolbar.vue';
import TableGutter from '@/components/TableGutter.vue';
import projectListCols from '@/components/table-cols/projectListCols.js';
import Projects from '@/models/Projects';
import { DialogProgrammatic } from '@/components/Dialog';
import CreateProjectModal from '@/components/modals/CreateProject.vue';
import ActivitySlider from '@/components/ActivitySlider.vue';
import NotifyUsers from '@/components/modals/NotifyUsers.vue';
import ActivityLog from '@/components/ActivityLog.vue';
import projectExcelCols from '@/components/excel-cols/projectsExcelCols.js';
import Locations from '@/models/Locations';
import Users from '../models/Users';
import Validation from '../components/utils/Validations';
import ExcelImport from '@/components/ExcelImport.vue';

export default {
  name: 'Projects',
  components: {
    DataTable,
    TableToolbar,
    TableGutter,
    'create-project': CreateProjectModal,
    ActivitySlider,
    'notify-users': NotifyUsers,
    'activity-log': ActivityLog,
    ExcelImport,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const toast = useToast();
    const data = reactive({
      tableColOpts: projectListCols,
      showCreateProjectModal: false,
      showExcelModal: false,
      allProjs: [],
      indexedCustomIds: {},
      inventoryLocations: {},
      existingUsers: [],
      nonExistingCompanyUserEmails: [],
      showNotifyUsersModal: false,
      showActiveOnly: true,
      isActivityLogOpen: false,
      selectedNotifyCard: {},
      activityDetail: {
        id: '',
        projectId: '',
        orderName: '',
      },
      searchVal: '',
      reRender: 0,
      isActivityActive: false,
    });

    const companyId = computed(() => store.state.userData?.company || '');

    const onToggleActiveOnly = () => {
      data.reRender += 1;
    };

    const getLinkedStatus = (project) => {
      const companyId = store.state.userData.company?._id || store.state.userData.company;
      const setting = project.projectSettings?.find((s) => s.companyId === companyId);
      return setting?.status || project.status;
    };

    const loadData = async (commonParam) => {
      const projects = await Projects.allProjects({
        companyId: store.state.userData.company,
        ...commonParam,
        showArchived: !data.showActiveOnly,
        sort: {
          [commonParam.sort[0].sortField]:
            commonParam.sort[0].direction === 'asc' ? 1 : -1,
        },
        searchText: data.searchVal,
      });

      projects.forEach((p) => {
        p.status = getLinkedStatus(p);
      });

      const projectGroups = groupBy(projects, (proj) => {
        if (isEmpty(proj.parentProject)) return 'individual';
        return proj.parentProject._id || proj.parentProject;
      });
      return {
        data: map(projectGroups.individual, (project) => {
          project.items = projectGroups[project._id.toString()] || [];
          // project.actionMenuOpen = false;
          return project;
        }),
        total: projects.length ? 10000 : 0,
      };
    };

    const refreshTable = () => {
      data.reRender++;
    };

    const mapData = async (projects) => {
      if (_.isEmpty(data.inventoryLocations)) {
        const locations = [];
        const [GI] = await Projects.allProjects({ isGI: true });
        if (GI) {
          const locs = await Locations.allLocationForUser({
            companyId: store.state.userData.company,
            project: [GI._id],
          });
          locations.push(...locs);
        } else {
          const compLocations = (await Locations.allCompanyLocations({
            companyId: store.state.userData.company,
            type: 'company_location',
          })).data;
          locations.push(...compLocations);
        }
        let allLocations = _.uniq(locations);
        allLocations = _.keyBy(allLocations, 'name');
        data.inventoryLocations = Object.keys(allLocations).reduce((acc, key) => {
          acc[key.toLowerCase()] = allLocations[key];
          return acc;
        }, {});
      }
      const emails = _.compact(_.map(projects, (p) => p.email || null));
      data.existingUsers = (await Users.allUsers({ emails })).data;

      data.allProjs = await Projects.allProjects();
      data.indexedCustomIds = {};
      _.forEach(data.allProjs, (proj) => {
        if (proj.isTemplateProject) return;
        _.forEach(proj.projectSettings, (proSett) => {
          if (proSett.companyId === proj.createdCompany._id) {
            data.indexedCustomIds[proSett.customId ? proSett.customId.toLowerCase() : ''] = proj._id;
          }
        });
      });

      return projects;
    };

    const saveExcelItem = async (excelProj) => {
      let createdProject = {};
      const presentProId = data.indexedCustomIds[excelProj.proId.toLowerCase()];
      const isNonCurrCompanyEmail = data.nonExistingCompanyUserEmails.includes(excelProj.email);

      if (!presentProId && !isNonCurrCompanyEmail) {
        const projectSetting = {
          toNotify: true,
          notifications: 1,
          isPrivate: true,
          isPTEnabled: true,
          status: 'Active',
          customId: excelProj.proId || undefined,
        };
        const projectObj = {
          projectSettings: [projectSetting],
          location: {},
          linkedProjectSetting: projectSetting,
          createdCompany: store.state.userData.company,
          contactEmail: store.state.userData.email,
          status: 'Active',
          projectLocations: [],
        };
        _.assign(projectObj, _.pick(excelProj, ['name', 'email']));
        if (!_.isEmpty(excelProj.jobsiteName)) {
          projectObj.location.name = excelProj.jobsiteName;
        }
        _.assign(projectObj.location, _.pick(excelProj, ['street', 'city', 'state', 'zipcode']));
        if (!_.isEmpty(projectObj.location)) {
          projectObj.projectLocations.push(projectObj.location);
        }
        if (!_.isEmpty(excelProj.projInvLoc)) {
          projectObj.projectSettings[0].projectInventoryLocation = data.inventoryLocations[excelProj.projInvLoc.toLowerCase()];
        }

        createdProject = await Projects.update(projectObj);
        data.allProjs.push(createdProject);
        data.indexedCustomIds[excelProj.proId.toLowerCase()] = createdProject._id;
      } else {
        createdProject = _.find(data.allProjs, { _id: presentProId });
        createdProject.location.name = excelProj.jobsiteName || _.get(createdProject, 'location.name', '');
        createdProject.name = excelProj.name || createdProject.name;
        _.assign(createdProject.location, _.pick(excelProj, ['street', 'city', 'state', 'zipcode']));
        if (!_.isEmpty(excelProj.projInvLoc)) {
          createdProject.projectSettings[0].projectInventoryLocation = data.inventoryLocations[excelProj.projInvLoc.toLowerCase()];
        }
        createdProject.linkedProjectSetting = _.find(
          createdProject.projectSettings,
          (setting) => setting.companyId === (store.state.userData.company._id || store.state.userData.company),
        );
        await Projects.update(createdProject);
      }

      if (_.find(createdProject.projectUsers, { 'usr.user.email': excelProj.email })) {
        return true;
      }
      if (_.isEmpty(excelProj.email)) return true;

      return Projects.addUserToProject({
        emails: [excelProj.email],
        projectId: createdProject._id,
        title: 'User',
        fromExcel: true,
      });
    };

    const validateProject = async (obj) => {
      try {
        obj.proId = _.toString(obj.proId);
        obj.zipcode = _.toString(obj.zipcode);
        let errMsg = null;
        const nameMap = {
          name: 'Name',
          proId: 'Project ID',
          jobsiteName: 'Location name',
          street: 'Street name',
          city: 'City name',
          state: 'State name',
        };
        for (const field of _.keys(nameMap)) {
          let minChar = 3;
          if (field === 'state') minChar = 2;
          if (_.isEmpty(obj[field]) || (obj[field] && _.trim(obj[field]).length < minChar)) {
            errMsg = `${nameMap[field]} is Mandatory and ${nameMap[field]} should be atleast ${minChar} characters long`;
          }
        }

        if (!Validation.validateProjectId(obj.proId)) {
          errMsg = 'Please enter valid Project ID (Project ID can only contain 3 to 16 alpha-numeric characters and dashes)';
        }

        if (!errMsg) {
          if (obj.email && !Validation.validateEmail(obj.email)) {
            errMsg = 'Member Field must have a valid email!';
          } else if (_.isEmpty(obj.zipcode) || (!_.isEmpty(obj.zipcode) && !Validation.validateZip(_.trim(obj.zipcode)))) {
            errMsg = 'Valid range for zip length is 1 - 24!';
          } else if (!_.isEmpty(obj.projInvLoc) && !_.keys(data.inventoryLocations).includes(obj.projInvLoc.toLowerCase())) {
            errMsg = 'Entered location do not match with Company or GI locations';
          } else if (!_.isEmpty(obj.email)) {
            if (_.isEmpty(data.existingUsers)) {
              const allUsers = await Users.getUsers({ email: obj.email });
              const currCompanyUser = allUsers.filter((u) => u.company === store.state.userData.company);
              if (!_.isEmpty(allUsers) && _.isEmpty(currCompanyUser)) {
                data.nonExistingCompanyUserEmails.push(obj.email);
                errMsg = 'Entered email is already a part of other company';
              }
            }
            const archivedUsers = data.existingUsers.filter((u) => u.email === obj.email && u.archived);
            if (archivedUsers.length) {
              errMsg = 'Entered email already exists';
            }
          }
        }

        return errMsg;
      } catch (e) {
        console.error('validateProject error:', e);
        return 'Internal validation error - check console';
      }
    };

    const setModalActive = (key, val) => {
      data[key] = val;
    };

    const searchData = (val) => {
      if (
        (!isEmpty(data.searchVal) && isEmpty(val))
        || (!isEmpty(val) && data.searchVal !== val)
      ) {
        data.searchVal = val;
        data.reRender++;
      }
    };

    const handleAction = async (action) => {
      const actionType = action?.type ?? action;
      const actionData = action?.data;

      const actionMapper = {
        addNew: () => setModalActive('showCreateProjectModal', true),
        openActivity: () => setModalActive('isActivityActive', true),
        excelImport: () => setModalActive('showExcelModal', true),
        search: (val) => searchData(val),
      };
      try {
        await actionMapper[actionType]?.(actionData);
      } catch (e) {
        toast.error(e?.data?.message || 'Project error');
        console.error('Error: ', e);
      }
    };  
    onBeforeMount(async () => {
      await store.getters.userPromise;
    });

    const mapProjectForNotify = (project) => {
      const companyId = store.state.userData.company;
      return {
        ...project,
        project: project?.project || project,
        owner: project?.owner || { company: { _id: companyId } },
      };
    };

    const archiveProject = (project) => {
      DialogProgrammatic.confirm({
        title: 'Are you Sure?',
        message: 'This project will be archived',
        okButton: 'Yes',
        cancelButton: 'Cancel',
        type: 'danger',
        onConfirm: async () => {
          try {
            data.isLoading = true;
            await Projects.archive({ id: project._id });
            data.reRender++;
          } catch (error) {
            console.error('Error archiving project:', error);
          } finally {
            data.isLoading = false;
          }
        },
      });
    };

    const onCellClicked = (event) => {
      const { type, data: rowData } = event;
      if (type === 'openCard') {
        const routeData = {
          newTab: '_blank',
          sameTab: '_self',
        };
        const routeObj = {
          name: 'project-view',
          params: { id: rowData._id },
        };
        routeData.link = router.resolve(routeObj);
        window.open(routeData.link.href, routeData[store.state.userData.openPreference]);
      } else if (type === 'projectTeam') {
        router.push({ name: 'project-view', params: { id: rowData._id }, query: { tab: 'team' } });
      } else if (type === 'notifyUsers') {
        data.selectedNotifyCard = mapProjectForNotify(rowData);
        data.showNotifyUsersModal = true;
      } else if (type === 'activity') { 
        data.activityDetail = {
          id: rowData._id,
          projectId: rowData._id,
          orderName: rowData.name,
        };
        data.isActivityLogOpen = true;
      } else if (type === 'archiveProject') {
         archiveProject(rowData);
      }
    };

    return {
      ...toRefs(data),
      loadData,
      handleAction,
      onCellClicked,
      companyId,
      projectExcelCols,
      validateProject,
      mapData,
      saveExcelItem,
      refreshTable,
      onToggleActiveOnly,
    };
  },
};
</script>
